import { useEffect, useState } from 'react';
import "./styles/index.scss";
import OtpInput from 'react-otp-input';
import Foot from '@icons/Footer.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import Loading from '@src/components/Loading';
import { toast } from 'react-toastify';
import useEndpoint from '../auth/auth_endpoints';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const OtpScreen = () => {
  const [otp, setOtp] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const query = useQuery();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const verifyWithOtp = async () => {
    if (!otp) {
      toast.error('Please enter OTP');
      return;
    }

    if (otp.length !== 6) {
      toast.error('Please enter a valid OTP');
      return;
    }

    try {
      setLoading(true);
      const response = await useEndpoint().verifyEmail({ code: otp, email });
      console.log("OTP Response:", response);
      setLoading(false);

      if (response.statusCode === 1) {
        const { token, details, levelDetails } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify({
          id: details._id,
          fullname: details.fullname,
          email: details.email,
          profileImg: details.profileImg,
          status: details.status,
          level: details.level,
          levelDetails: levelDetails,
        }));
        // Check if the user has updated their password
        if (details.passwordUpdated) {
          // Await the dashboard data request
          // Navigate based on the user level
          const canManageAdmin = levelDetails.canManageAdmin;
          const canManageUsers = levelDetails.canManageUsers;
          const canManageAffilliates = levelDetails.canManageAffilliates;
          const canManageResources = levelDetails.canManageResources;
          const canManageTransactions = levelDetails.canManageTransactions;
          const canManageSchoolAI = levelDetails.canManageSchoolAI;
          const canManageSchoolAIPayments = levelDetails.canManageSchoolAIPayments;

          if ( canManageAdmin && canManageUsers && canManageTransactions ) {
            window.location.pathname = "/home/dashboard";
          }else if ( canManageAdmin ) {
            window.location.pathname = "/home/admins";
          }else if ( canManageUsers ) {
            window.location.pathname = "/home/all-users";
          } else if ( canManageAffilliates ) {
            window.location.pathname = "/home/affiliates";
          } else if ( canManageResources ) {
            window.location.pathname = "/home/resources";
          } else if ( canManageTransactions ) {
            window.location.pathname = "/home/payout";
          } else if ( canManageSchoolAI ) {
            window.location.pathname = "/home/school-ai";
          } else if ( canManageSchoolAIPayments ) {
            window.location.pathname = "/home/school-payment-links";
          } else {
            window.location.pathname = "/home/profile";
          }
        } else {
          // Redirect to profile update page if password is not updated
          window.location.href = "/home/profile?showPasswordModal=true";
        }


      } else {
        toast.error(response.message || 'Something went wrong');
      }
    } catch (error: any) {
      setLoading(false);
      toast.error(error?.message || 'Something went wrong');
    }
  };

  useEffect(() => {
    const emailExtracted = query.get('email') || '';
    setEmail(emailExtracted);

    navigate({
      pathname: window.location.pathname,
      search: ''
    }, { replace: true });
  }, []);

  return (
    loading ? (
      <Loading />
    ) : (
      <div className='min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4'>
        <div className='w-full max-w-md'>
          {/* Card Container */}
          <div className='bg-white rounded-2xl shadow-xl p-8 md:p-10'>
            {/* Icon */}
            <div className='flex justify-center mb-6'>
              <div className='w-16 h-16 bg-[#E55109]/10 rounded-full flex items-center justify-center'>
                <svg className='w-8 h-8 text-[#E55109]' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
                </svg>
              </div>
            </div>

            {/* Title */}
            <div className='text-center mb-8'>
              <h1 className='text-2xl md:text-3xl font-bold text-gray-900 mb-2'>Verify Your Email</h1>
              <p className='text-gray-600 text-sm md:text-base'>
                We've sent a 6-digit code to <span className='font-semibold text-gray-900'>{email}</span>
              </p>
            </div>

            {/* OTP Input */}
            <div className='mb-8'>
              <OtpInput
                value={otp}
                onChange={(value) => setOtp(value)}
                numInputs={6}
                inputStyle='otp-input-modern'
                renderInput={(props) => <input {...props} className='otp-input-modern' />}
              />
            </div>

            {/* Proceed Button */}
            <button 
              onClick={() => { verifyWithOtp() }} 
              disabled={otp.length !== 6}
              className='w-full bg-[#E55109] hover:bg-[#d14808] text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-lg shadow-[#E55109]/30 disabled:opacity-50 disabled:cursor-not-allowed mb-6'
            >
              Verify & Continue
            </button>

            {/* Resend Code */}
            <div className='text-center'>
              <p className='text-sm text-gray-600'>
                Didn't receive the code?{' '}
                <button className='text-[#E55109] hover:text-[#d14808] font-semibold transition-colors'>
                  Resend Code
                </button>
              </p>
            </div>
          </div>

          {/* Help Text */}
          <div className='text-center mt-6'>
            <p className='text-sm text-gray-600'>
              Need help?{' '}
              <a href='mailto:support@gmind.ai' className='text-[#E55109] hover:text-[#d14808] font-medium'>
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </div>
    )
  );
}

export default OtpScreen;
